import Axios, { type AxiosInstance, type AxiosResponse } from 'axios';
import { PDFDocument } from 'pdf-lib';

import ApiError from '@/core/lib/ApiError';
import { getConfig } from '@/core/lib/Config';
import { AuthenticationError, getAuthentication } from '@/domains/authentification/lib/Authentication';

export const ALPTIS_API_KEY = 'X-Alptis-Api-Key';

function handleErrorOnReject(axios: AxiosInstance): void {
  axios.interceptors.response.use(
    response => response,
    (error: unknown) => {
      if (error && Axios.isAxiosError(error) && error.response) {
        return Promise.reject(new ApiError(error));
      }

      if (error instanceof Error) {
        return Promise.reject(error);
      }

      return Promise.reject(new Error(String(error)));
    },
  );
}

async function useAuthBearerToken(axios: AxiosInstance): Promise<void> {
  const { updateToken, getState } = await getAuthentication();

  axios.interceptors.request.use(async config => {
    await updateToken();
    const state = getState();

    if (state.token === undefined) {
      throw new AuthenticationError('Authentication token is undefined');
    }

    config.headers.set('Authorization', `Bearer ${state.token}`);

    return config;
  });
}

function useApiKey(axios: AxiosInstance, keyValue: string): void {
  axios.interceptors.request.use(config => {
    config.headers.set(ALPTIS_API_KEY, keyValue);

    return config;
  });
}

export async function getEspaceDistributeurServicesApi(requiresAuth = true): Promise<AxiosInstance> {
  const {
    apiAlptis: { baseUrl, serviceEspaceDistributeur, apiKey },
  } = await getConfig();

  const axios = Axios.create({ baseURL: `${baseUrl}/${serviceEspaceDistributeur}` });

  if (requiresAuth) {
    await useAuthBearerToken(axios);
  } else {
    useApiKey(axios, apiKey);
  }

  handleErrorOnReject(axios);

  return axios;
}

export async function getEspaceDistributeurApi(): Promise<AxiosInstance> {
  const { espaceDigitalHomeUrl } = await getConfig();

  const axios = Axios.create({ baseURL: espaceDigitalHomeUrl });

  await useAuthBearerToken(axios);

  return axios;
}

export async function getEvcApi(): Promise<AxiosInstance> {
  const { evcApiBaseUrl } = await getConfig();

  const axios = Axios.create({ baseURL: evcApiBaseUrl });

  await useAuthBearerToken(axios);

  return axios;
}

export function parseTotalFromContentRange(contentRangeString: string | undefined): number {
  if (!contentRangeString) {
    return 0;
  }

  const total = Number.parseInt(contentRangeString.substring(contentRangeString.indexOf('/') + 1));

  return Number.isNaN(total) ? 0 : total;
}

export function assembleRangeHeader(rangeStart: number, rangeEnd: number, unit = 'items'): string {
  return `${unit}=${rangeStart.toString()}-${rangeEnd.toString()}`;
}

export function getRangePartsForPage(page: number, itemsPerPage: number): { rangeStart: number; rangeEnd: number } {
  return {
    rangeStart: (page * itemsPerPage) + 1,
    rangeEnd: (page + 1) * itemsPerPage,
  };
}

export function assembleRangeHeaderForPage(page: number, itemsPerPage: number, unit?: string): string {
  const { rangeStart, rangeEnd } = getRangePartsForPage(page, itemsPerPage);

  return assembleRangeHeader(rangeStart, rangeEnd, unit);
}

export function parseFilenameFromContentDisposition(contentDisposition: string | undefined): string | undefined {
  if (!contentDisposition) {
    return undefined;
  }

  const matches = Array.from(contentDisposition.matchAll(/filename\*?\s*=\s*"?(?:utf-8'')?([^";]+)"?/gi));

  // Priority to the utf-8 match
  const utf8Match = matches.find(([source]) => /utf-8/i.exec(source));

  if (utf8Match?.[1] !== undefined) {
    return decodeURIComponent(utf8Match[1]);
  }

  // Otherwise the first match
  const firstMatch = matches.find(([, match]) => !!match);

  if (firstMatch !== undefined) {
    return firstMatch[1];
  }

  return undefined;
}

export async function getFileFromResponseOrFail(
  response: AxiosResponse<ArrayBuffer | undefined, unknown>,
  forcedFileName?: string,
): Promise<File> {
  const getFileContent = (): ArrayBuffer => {
    if (!response.data) {
      throw new Error('No file content.');
    }

    return response.data;
  };

  const getFileType = (): string => {
    const fileType = response.headers['content-type'] as string | undefined;

    if (!fileType) {
      throw new Error('File type not found in Content-Type header.');
    }

    return fileType;
  };

  const getFileName = (): string => {
    if (forcedFileName) {
      return forcedFileName;
    }

    const fileName = parseFilenameFromContentDisposition(
      response.headers['content-disposition'] as string | undefined,
    );

    if (!fileName) {
      throw new Error('File name not found in Content-Disposition header.');
    }

    return fileName;
  };

  const fileType = getFileType();
  const fileName = getFileName();
  const fileContent = getFileContent();

  const getBlobParts = async (): Promise<ArrayBuffer[]> => {
    if (fileType.includes('application/pdf')) {
      // Sync pdf filename with pdf metadata title if empty
      const pdfDocument = await PDFDocument.load(fileContent);

      if (!pdfDocument.getTitle()) {
        pdfDocument.setTitle(fileName);

        const newFileContent = new Uint8Array(await pdfDocument.save()).buffer;

        return [newFileContent];
      }
    }

    return [fileContent];
  };

  return new File(await getBlobParts(), fileName, { type: fileType });
}
